
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Upload, FileText, X, Check, Loader2, ArrowRight } from "lucide-react";
import { useDocuments } from "@/hooks/use-documents";
import { useComparisons } from "@/hooks/use-comparisons";
import { Document } from "@/api/generated/client/models";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { usePdfUtils } from "@/hooks/use-pdf-utils";
import { apiClient } from "@/lib/api-client";
import { CostConfirmationDialog, CostEstimate } from "@/components/claims/create-claim/CostConfirmationDialog";

interface ComparisonModalProps {
    claimId: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type ModalStep = 'selection' | 'costConfirmation';

export function ComparisonModal({ claimId, open, onOpenChange }: ComparisonModalProps) {
    const { documents, uploadDocumentAsync, isUploading } = useDocuments(claimId);
    const { createComparisonAsync, isCreating } = useComparisons(claimId);
    const { toast } = useToast();
    const { countPages } = usePdfUtils();

    const [selectedInsurerDoc, setSelectedInsurerDoc] = useState<Document | null>(null);
    const [selectedContractorDoc, setSelectedContractorDoc] = useState<Document | null>(null);
    const [newInsurerFile, setNewInsurerFile] = useState<File | null>(null);
    const [newContractorFile, setNewContractorFile] = useState<File | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Cost estimation state
    const [currentStep, setCurrentStep] = useState<ModalStep>('selection');
    const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null);
    const [isEstimating, setIsEstimating] = useState(false);

    // Refs for hidden inputs
    const insurerInputRef = useRef<HTMLInputElement>(null);
    const contractorInputRef = useRef<HTMLInputElement>(null);

    // Filter documents by kind
    const insurerDocs = documents.filter(d => d.kind === 'insurer');
    const contractorDocs = documents.filter(d => d.kind === 'contractor');

    // Reset state when closing
    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            setSelectedInsurerDoc(null);
            setSelectedContractorDoc(null);
            setNewInsurerFile(null);
            setNewContractorFile(null);
            setIsProcessing(false);
            setCurrentStep('selection');
            setCostEstimate(null);
            setIsEstimating(false);
        }
        onOpenChange(newOpen);
    };

    // --- Handlers ---
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'insurer' | 'contractor') => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                toast({ title: "Invalid file", description: "Only PDF files are allowed", variant: "destructive" });
                return;
            }
            if (type === 'insurer') {
                setNewInsurerFile(file);
                setSelectedInsurerDoc(null);
            } else {
                setNewContractorFile(file);
                setSelectedContractorDoc(null);
            }
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, type: 'insurer' | 'contractor') => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                toast({ title: "Invalid file", description: "Only PDF files are allowed", variant: "destructive" });
                return;
            }
            if (type === 'insurer') {
                setNewInsurerFile(file);
                setSelectedInsurerDoc(null);
            } else {
                setNewContractorFile(file);
                setSelectedContractorDoc(null);
            }
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    /**
     * Fetch PDF file content for an existing document and count its pages
     */
    const getDocumentPageCount = async (documentId: number): Promise<number> => {
        const blob = await apiClient.documents.getDocumentContent({ documentId });
        const file = new File([blob], 'document.pdf', { type: 'application/pdf' });
        return await countPages(file);
    };

    /**
     * Estimate the cost before running the comparison
     */
    const estimateCost = async () => {
        setIsEstimating(true);
        try {
            const pageCounts: number[] = [];
            let insPages = 0;
            let conPages = 0;

            // Get insurance document page count
            if (newInsurerFile) {
                insPages = await countPages(newInsurerFile);
            } else if (selectedInsurerDoc) {
                insPages = await getDocumentPageCount(selectedInsurerDoc.id);
            }
            pageCounts.push(insPages);

            // Get contractor document page count
            if (newContractorFile) {
                conPages = await countPages(newContractorFile);
            } else if (selectedContractorDoc) {
                conPages = await getDocumentPageCount(selectedContractorDoc.id);
            }
            pageCounts.push(conPages);

            // Call billing API to get cost estimate
            const estimate = await apiClient.billing.estimateBillingFromPageCounts({
                estimateBillingFromPageCountsRequest: { pageCounts }
            });

            // Use page counts from API response - fail if missing
            if (!estimate.documents[0] || estimate.documents[0].pages === undefined) {
                throw new Error('API response missing insurance document page count');
            }
            if (!estimate.documents[1] || estimate.documents[1].pages === undefined) {
                throw new Error('API response missing contractor document page count');
            }
            const insurancePages = estimate.documents[0].pages;
            const contractorPages = estimate.documents[1].pages;

            if (!estimate.tierCode) {
                throw new Error('API response missing tier code');
            }

            setCostEstimate({
                totalCredits: estimate.totalCredits,
                totalCostCents: estimate.totalCostCents,
                tierCode: estimate.tierCode,
                totalPages: estimate.totalPages,
                insurancePages,
                contractorPages,
                costPerCreditCents: estimate.costPerCreditCents
            });

            setCurrentStep('costConfirmation');

        } catch (error) {
            console.error("Cost estimation error:", error);
            toast({
                title: "Estimation failed",
                description: "Failed to estimate processing cost. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsEstimating(false);
        }
    };

    const runComparison = async () => {
        setIsProcessing(true);
        try {
            let insurerDocId = selectedInsurerDoc?.id;
            let contractorDocId = selectedContractorDoc?.id;

            // 1. Upload new insurer file if needed
            if (newInsurerFile) {
                // Comparison uploads should NOT send notifications (reduces noise)
                const doc = await uploadDocumentAsync({ file: newInsurerFile, kind: 'insurer', notify: false });
                insurerDocId = doc.id;
            }

            // 2. Upload new contractor file if needed
            if (newContractorFile) {
                // Comparison uploads should NOT send notifications (reduces noise)
                const doc = await uploadDocumentAsync({ file: newContractorFile, kind: 'contractor', notify: false });
                contractorDocId = doc.id;
            }

            if (!insurerDocId || !contractorDocId) {
                toast({ title: "Error", description: "Missing document IDs", variant: "destructive" });
                return;
            }

            // 3. Create Comparison
            await createComparisonAsync({
                insurerDocumentId: insurerDocId,
                contractorDocumentId: contractorDocId
            });

            toast({ title: "Success", description: "Comparison started successfully" });
            handleOpenChange(false);

        } catch (error) {
            console.error(error);
            toast({ title: "Error", description: "Failed to start comparison", variant: "destructive" });
        } finally {
            setIsProcessing(false);
        }
    };

    const hasInsurerSelection = !!selectedInsurerDoc || !!newInsurerFile;
    const hasContractorSelection = !!selectedContractorDoc || !!newContractorFile;
    const canProceedToCost = hasInsurerSelection && hasContractorSelection && !isProcessing && !isUploading && !isCreating && !isEstimating;

    // Render cost confirmation step using shared dialog
    if (currentStep === 'costConfirmation' && costEstimate) {
        return (
            <CostConfirmationDialog
                open={open}
                onOpenChange={handleOpenChange}
                estimate={costEstimate}
                onConfirm={runComparison}
                onBack={() => setCurrentStep('selection')}
                isProcessing={isProcessing}
                confirmLabel="Start Comparison"
            />
        );
    }

    // Render selection step
    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90dvh] md:max-h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 py-4 border-b">
                    <DialogTitle>Run New Comparison</DialogTitle>
                    <DialogDescription>
                        Select estimates to compare. You can choose existing documents or upload new ones.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-hidden p-6 grid grid-cols-2 gap-6 min-h-0">
                    {/* LEFT: Insurer */}
                    <div className="flex flex-col h-full overflow-hidden">
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-4 shrink-0">
                            <FileText className="h-4 w-4 text-blue-600" />
                            Insurer Estimate
                            {hasInsurerSelection && <Check className="h-4 w-4 text-green-600 ml-auto" />}
                        </h3>

                        <div className="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2">
                            {insurerDocs.length > 0 && !newInsurerFile ? (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Select existing:</Label>
                                    <div className="space-y-2">
                                        {insurerDocs.map(doc => (
                                            <Card
                                                key={doc.id}
                                                className={cn(
                                                    "p-3 cursor-pointer text-sm transition-all border-l-4",
                                                    selectedInsurerDoc?.id === doc.id
                                                        ? "border-l-blue-600 border-gray-200 bg-blue-50/50 shadow-sm"
                                                        : "border-l-transparent hover:border-l-gray-300 hover:bg-muted/50"
                                                )}
                                                onClick={() => { setSelectedInsurerDoc(doc); setNewInsurerFile(null); }}
                                            >
                                                <div className="font-medium truncate">{doc.filename}</div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    {format(new Date(doc.createdAt), 'MMM d, yyyy')} • {doc.mimeType === 'application/pdf' ? 'PDF' : 'Doc'}
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm border border-dashed rounded-lg bg-muted/10 p-4">
                                    {newInsurerFile ? (
                                        <div className="text-center">
                                            <p className="font-medium text-foreground">File selected</p>
                                            <p className="text-xs mt-1">Ready to upload</p>
                                        </div>
                                    ) : (
                                        <p>No existing documents found</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="mt-4 shrink-0 pt-4 border-t">
                            {!selectedInsurerDoc && (
                                <div className="space-y-2">
                                    {insurerDocs.length > 0 && <div className="text-xs text-center text-muted-foreground">- OR -</div>}
                                    <input type="file" ref={insurerInputRef} className="hidden" accept=".pdf" onChange={(e) => handleFileSelect(e, 'insurer')} />
                                    {newInsurerFile ? (
                                        <Card className="p-3 flex items-center justify-between bg-blue-50/30 border-blue-100">
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                                <span className="text-sm truncate">{newInsurerFile.name}</span>
                                            </div>
                                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setNewInsurerFile(null)}>
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </Card>
                                    ) : (
                                        <div
                                            className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors bg-muted/20 hover:border-blue-400 hover:bg-blue-50/10"
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, 'insurer')}
                                            onClick={() => insurerInputRef.current?.click()}
                                        >
                                            <Upload className="h-6 w-6 text-muted-foreground mb-2" />
                                            <p className="text-xs text-muted-foreground">Drag PDF here or click to upload</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Contractor */}
                    <div className="flex flex-col h-full overflow-hidden">
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-4 shrink-0">
                            <FileText className="h-4 w-4 text-orange-600" />
                            Contractor Estimate
                            {hasContractorSelection && <Check className="h-4 w-4 text-green-600 ml-auto" />}
                        </h3>

                        <div className="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2">
                            {contractorDocs.length > 0 && !newContractorFile ? (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Select existing:</Label>
                                    <div className="space-y-2">
                                        {contractorDocs.map(doc => (
                                            <Card
                                                key={doc.id}
                                                className={cn(
                                                    "p-3 cursor-pointer text-sm transition-all border-l-4",
                                                    selectedContractorDoc?.id === doc.id
                                                        ? "border-l-orange-600 border-gray-200 bg-orange-50/50 shadow-sm"
                                                        : "border-l-transparent hover:border-l-gray-300 hover:bg-muted/50"
                                                )}
                                                onClick={() => { setSelectedContractorDoc(doc); setNewContractorFile(null); }}
                                            >
                                                <div className="font-medium truncate">{doc.filename}</div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    {format(new Date(doc.createdAt), 'MMM d, yyyy')} • {doc.mimeType === 'application/pdf' ? 'PDF' : 'Doc'}
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm border border-dashed rounded-lg bg-muted/10 p-4">
                                    {newContractorFile ? (
                                        <div className="text-center">
                                            <p className="font-medium text-foreground">File selected</p>
                                            <p className="text-xs mt-1">Ready to upload</p>
                                        </div>
                                    ) : (
                                        <p>No existing documents found</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="mt-4 shrink-0 pt-4 border-t">
                            {!selectedContractorDoc && (
                                <div className="space-y-2">
                                    {contractorDocs.length > 0 && <div className="text-xs text-center text-muted-foreground">- OR -</div>}
                                    <input type="file" ref={contractorInputRef} className="hidden" accept=".pdf" onChange={(e) => handleFileSelect(e, 'contractor')} />
                                    {newContractorFile ? (
                                        <Card className="p-3 flex items-center justify-between bg-orange-50/30 border-orange-100">
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <FileText className="h-4 w-4 text-orange-600 flex-shrink-0" />
                                                <span className="text-sm truncate">{newContractorFile.name}</span>
                                            </div>
                                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setNewContractorFile(null)}>
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </Card>
                                    ) : (
                                        <div
                                            className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors bg-muted/20 hover:border-orange-400 hover:bg-orange-50/10"
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, 'contractor')}
                                            onClick={() => contractorInputRef.current?.click()}
                                        >
                                            <Upload className="h-6 w-6 text-muted-foreground mb-2" />
                                            <p className="text-xs text-muted-foreground">Drag PDF here or click to upload</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter className="px-6 py-4 border-t bg-muted/10">
                    <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isProcessing || isEstimating}>
                        Cancel
                    </Button>
                    <Button onClick={estimateCost} disabled={!canProceedToCost} className="gap-2">
                        {isEstimating ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Estimating...
                            </>
                        ) : (
                            <>
                                Review Cost
                                <ArrowRight className="h-4 w-4" />
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
